import axios, { InternalAxiosRequestConfig } from 'axios'

import { CHAIN_IDS } from '@src/configs/chainInfo'
import { FEATHER_API_URL } from '@src/configs/env'
import {
    FeatherBackendStatus,
    IFeatherApiQuotePayload,
    IFeatherApiQuoteResponse,
    IFeatherApiSignedQuoteResponse,
    IFeatherOrderCreateResponse,
    IFeatherOrderStatusResponse,
    IFeatherQuoteSignPayload,
} from '@src/types/feather'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('feather.api')

export const featherAPI = axios.create({
    baseURL: FEATHER_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 60_000, // 60 seconds
})

/**
 * Raw API call to fetch a Feather quote - all pre/post processing should be done by the caller
 * @param payload The complete payload for the quote request
 */
export const apiCreateFeatherQuote = async (payload: IFeatherApiQuotePayload): Promise<IFeatherApiQuoteResponse> => {
    try {
        const response = await featherAPI.post<IFeatherApiQuoteResponse>('/v1/quotes/create', payload)
        return response.data
    } catch (error: any) {
        const errorDetails = {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            code: error.code,
        }
        logger.error('Error fetching Feather quote:', errorDetails)
        throw error
    }
}

/**
 * Sign a Feather quote with the user's wallet signature
 * @param quoteId The ID of the quote to sign
 * @param signature The signature from the user's wallet
 * @param srcUserWallet The user's source wallet address
 */
export const apiSignFeatherQuote = async (
    quoteId: string,
    signature: string,
    srcUserWallet: string
): Promise<IFeatherApiSignedQuoteResponse> => {
    try {
        logger.info('Signing Feather quote', { quoteId, srcUserWallet })

        const payload: IFeatherQuoteSignPayload = {
            quote_id: quoteId,
            signature,
            src_user_wallet: srcUserWallet,
        }

        logger.info('Sending quote signature to Feather API', { payload })
        const response = await featherAPI.post<IFeatherApiSignedQuoteResponse>('/v1/quotes/sign', payload)
        logger.info('Successfully signed Feather quote', { quoteId, status: response.data.status })

        return response.data
    } catch (error: any) {
        const errorDetails = {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            code: error.code,
        }
        logger.error('Error signing Feather quote:', errorDetails)
        throw error
    }
}

/**
 * Create a Feather order
 * @param quoteId The ID of the quote to create an order from
 * @param srcUserWallet The user's source wallet address (optional)
 */
export const apiCreateFeatherOrder = async (
    quoteId: string,
    srcUserWallet?: string
): Promise<IFeatherOrderCreateResponse> => {
    try {
        logger.info('Creating Feather order', { quoteId, srcUserWallet })

        // Create the payload with the quote ID and optionally the source wallet
        const payload: Record<string, string> = {
            quote_id: quoteId,
        }

        // Add source wallet if provided
        if (srcUserWallet) {
            payload.src_user_wallet = srcUserWallet
        }

        logger.info('Sending request to create Feather order', { payload })
        const response = await featherAPI.post('/v1/orders/create', payload)
        logger.info('Received response from Feather API', { data: response.data })

        // Get the chain ID from the chain name
        const srcChainName = (response.data.src_chain || '').toLowerCase()
        const chainId = CHAIN_IDS[srcChainName as keyof typeof CHAIN_IDS] || 0

        // Transform response to match our internal format
        const formattedResponse: IFeatherOrderCreateResponse = {
            orderId: response.data.order_id,
            depositAddress: response.data.src_deposit_wallet || '',
            memoId: response.data.memo_id,
            expiresAt: response.data.expires_at || Math.floor(Date.now() / 1000) + 3600, // Default 1 hour
            amountToSend: response.data.src_amount || '0',
            tokenAddress: srcUserWallet || response.data.src_token_address || '',
            chainId: chainId,
        }

        logger.info('Successfully created Feather order', {
            orderId: formattedResponse.orderId,
            depositAddress: formattedResponse.depositAddress,
            chainId: formattedResponse.chainId,
        })

        return formattedResponse
    } catch (error: any) {
        const errorDetails = {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            code: error.code,
        }
        logger.error('Error creating Feather order:', errorDetails)
        throw error
    }
}

/**
 * Get the status of a Feather order
 * @param orderId The ID of the order
 */
export const apiGetFeatherOrderStatus = async (orderId: string): Promise<IFeatherOrderStatusResponse> => {
    try {
        logger.info('Fetching Feather order status', { orderId })

        // Use the v1/orders/{id} endpoint
        const response = await featherAPI.get(`/v1/orders/${orderId}`)
        logger.info('Received response from Feather API', { data: response.data })

        // Return the formatted response
        const mappedResponse = mapOrderStatusResponse(response.data)
        logger.info('Successfully mapped order status', { status: mappedResponse.status })
        return mappedResponse
    } catch (error: any) {
        const errorDetails = {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            code: error.code,
        }
        logger.error('Error fetching Feather order status:', errorDetails)
        throw error
    }
}

/**
 * Update a Feather order with a deposit transaction hash
 * @param orderId The ID of the order
 * @param depositTxHash The transaction hash of the deposit
 */
export const apiUpdateFeatherOrderWithDepositDetails = async (
    orderId: string,
    depositTxHash: string
): Promise<IFeatherOrderStatusResponse> => {
    try {
        logger.info('Updating Feather order with deposit', { orderId, depositTxHash })

        const payload = {
            src_deposit_tx: depositTxHash,
        }

        logger.info('Sending update request to Feather API', { payload })
        // Use the v1/orders/{id} endpoint with PUT method
        const response = await featherAPI.put(`/v1/orders/${orderId}`, payload)
        logger.info('Received response from Feather API', { data: response.data })

        // Return the formatted response
        const mappedResponse = mapOrderStatusResponse(response.data)
        logger.info('Successfully updated order with deposit', { status: mappedResponse.status })
        return mappedResponse
    } catch (error: any) {
        const errorDetails = {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            code: error.code,
        }
        logger.error('Error updating Feather order with deposit:', errorDetails)
        throw error
    }
}

/**
 * Get orders for a specific wallet address
 * @param walletAddress The wallet address to get orders for
 */
export const apiGetFeatherOrdersByWallet = async (walletAddress: string): Promise<IFeatherOrderStatusResponse[]> => {
    try {
        logger.info('Fetching Feather orders for wallet', { walletAddress })

        // Use the v1/orders endpoint with wallet parameter
        const response = await featherAPI.get(`/v1/orders?wallet=${walletAddress}`)
        logger.info('Received response from Feather API', {
            orderCount: response.data.length,
            data: response.data,
        })

        // Map each order to the expected format
        const mappedOrders = response.data.map((order: any) => mapOrderStatusResponse(order))
        logger.info('Successfully mapped wallet orders', { orderCount: mappedOrders.length })
        return mappedOrders
    } catch (error: any) {
        const errorDetails = {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            code: error.code,
        }
        logger.error('Error getting Feather orders for wallet:', errorDetails)
        throw error
    }
}

/**
 * Create a Feather order with deposit transaction details
 * @param quoteId The ID of the quote to create an order from
 * @param srcUserWallet The user's source wallet address
 * @param depositTxHash The transaction hash of the deposit
 */
export const apiCreateFeatherOrderWithDepositDetails = async (
    quoteId: string,
    srcUserWallet: string,
    depositTxHash: string
): Promise<IFeatherOrderStatusResponse> => {
    try {
        logger.info('Creating Feather order with deposit transaction', {
            quoteId,
            srcUserWallet,
            depositTxHash,
        })

        // Create the payload with the quote ID, source wallet, and deposit transaction
        const payload = {
            quote_id: quoteId,
            src_user_wallet: srcUserWallet,
            src_deposit_tx: depositTxHash,
        }

        logger.info('Sending request to create Feather order with deposit', { payload })
        const response = await featherAPI.post('/v1/orders/create', payload)
        logger.info('Received response from Feather API', { data: response.data })

        // Return the formatted response
        const mappedResponse = mapOrderStatusResponse(response.data)
        logger.info('Successfully created Feather order with deposit', {
            orderId: mappedResponse.orderId,
            status: mappedResponse.status,
        })

        return mappedResponse
    } catch (error: any) {
        const errorDetails = {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            code: error.code,
        }
        logger.error('Error creating Feather order with deposit:', errorDetails)
        throw error
    }
}

// Helper function to map order status response
const mapOrderStatusResponse = (data: any): IFeatherOrderStatusResponse => {
    // Map processing history if available
    const processingHistory = data.processing_history || []

    // Directly map fields from backend to frontend without any transformations
    return {
        orderId: data.order_id,
        status: mapStatus(data.status),
        createdAt: data.created_at || new Date().toISOString(),
        updatedAt: data.updated_at || new Date().toISOString(),
        timeout: data.timeout || 3600, // Default 1 hour if not provided
        src_deposit_tx: data.src_deposit_tx, // This may be null
        dst_fill_tx: data.dst_fill_tx, // This may be null
        processingHistory: processingHistory.map((entry: any) => ({
            timestamp: entry.timestamp,
            status: mapStatus(entry.status),
            message: entry.message,
        })),
        quoteId: data.quote_id || '',
        srcTicker: data.src_ticker || '',
        srcChain: data.src_chain || '',
        dstTicker: data.dst_ticker || '',
        dstChain: data.dst_chain || '',
        srcUserWallet: data.src_user_wallet || '',
        dstUserWallet: data.dst_user_wallet || '',
        srcDepositWallet: data.src_deposit_wallet || '',
        srcAmount: data.src_amount || '0',
        dstAmount: data.dst_amount || '0',
        quoteExpiresAt: data.quote_expires_at || new Date(Date.now() + 3600000).toISOString(), // Default 1 hour from now
    }
}

// Helper function to map status string to backend enum
const mapStatus = (status: string): FeatherBackendStatus => {
    // If the status is already one of our known backend statuses, return it directly
    if (Object.values(FeatherBackendStatus).includes(status as FeatherBackendStatus)) {
        return status as FeatherBackendStatus
    }

    // Otherwise map from the old format to our new backend format
    switch (status) {
        case 'pending':
            return FeatherBackendStatus.AWAITING_DEPOSIT
        case 'deposit_detected':
            return FeatherBackendStatus.DEPOSIT_CONFIRMED
        case 'processing':
            return FeatherBackendStatus.PROCESSING
        case 'sending':
            return FeatherBackendStatus.WITHDRAWING
        case 'filled':
            return FeatherBackendStatus.COMPLETED
        case 'expired':
            return FeatherBackendStatus.DEPOSIT_TIMEOUT
        case 'failed':
            return FeatherBackendStatus.FAILED
        default:
            return FeatherBackendStatus.UNKNOWN
    }
}

featherAPI.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    logger.info('🔍 Request to Feather API', {
        baseURL: config.baseURL,
        url: config.url,
        method: config.method,
        data: config.data,
    })
    return config
})
